/**
 * PackageCard — pricing tile for a single Property Check package.
 *
 * Used on the public landing page (`/property-check`) and in Screen 1 of
 * the booking wizard. Compact, click-to-select, with a selected-state.
 */

import React from 'react';
import { Card, CardActionArea, CardContent, Typography, Box, Chip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import { colors } from '../../theme/colors';

const PackageCard = ({ pkg, selected, onSelect }) => {
  if (!pkg) return null;
  const isUrgent = pkg.isUrgent;
  const accent = isUrgent ? colors.brand.secondary : colors.brand.primary;

  return (
    <Card
      elevation={0}
      aria-pressed={!!selected}
      sx={{
        borderRadius: '12px',
        height: '100%',
        position: 'relative',
        // Same ring as the realtor / conveyancer cards; the chosen one gets a
        // heavier ring in its accent and a "Selected" tick, not a lift.
        boxShadow: selected ? `0 0 0 2px ${accent}` : '0 0 0 1px #dfe6e4',
        transition: 'box-shadow 160ms ease',
        '&:hover': { boxShadow: selected ? `0 0 0 2px ${accent}` : '0 0 0 1px #94a3b8' },
      }}
    >
      {selected && (
        <Box sx={{ position: 'absolute', top: 14, right: 14, zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, height: 26, borderRadius: '6px', bgcolor: accent, color: '#fff', fontSize: 13, fontWeight: 700, pointerEvents: 'none' }}>
          <CheckIcon sx={{ fontSize: 16 }} /> Selected
        </Box>
      )}
      <CardActionArea
        onClick={onSelect}
        sx={{ height: '100%', alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}
      >
        <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {isUrgent && (
              <BoltIcon fontSize="small" sx={{ color: accent, mr: 0.5 }} />
            )}
            <Typography
              variant="overline"
              sx={{
                color: accent,
                fontWeight: 700,
                letterSpacing: '0.04em',
              }}
            >
              {isUrgent ? 'Same-day' : `${pkg.turnaroundHours}h turnaround`}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}>
            {pkg.displayName}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: colors.text?.secondary || '#475569', mb: 2, flex: 1 }}
          >
            {pkg.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: accent }}>
              ${Number(pkg.basePriceUsd).toFixed(0)}
            </Typography>
            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
              USD
            </Typography>
          </Box>

          {pkg.availableCities && pkg.availableCities.length > 0 && (
            <Chip
              size="small"
              label={`Available in ${pkg.availableCities.join(', ')}`}
              sx={{
                mt: 1,
                bgcolor: colors.status?.warningBg || 'rgba(217, 119, 6, 0.08)',
                color: colors.status?.warningDark || '#b45309',
                fontWeight: 600,
                borderRadius: '6px',
                alignSelf: 'flex-start',
              }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default PackageCard;
